import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang, pick } from "../context/LanguageContext";
import { t } from "../data/translations";
import { resolveImg } from "../lib/api";

const Card = ({ item, index }) => {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.1 }}
      className={`group relative ${index % 3 === 1 ? "md:mt-16" : ""}`}
      data-testid={`featured-card-${index}`}
    >
      <div className="relative overflow-hidden rounded-[2rem] aspect-[4/5] bg-brand-surface">
        <motion.img
          style={{ y }}
          src={resolveImg(item.image_url)}
          alt={pick({ en: item.name_en, es: item.name_es }, lang)}
          className="absolute inset-0 w-full h-[120%] -top-[10%] object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl leading-none">{pick({ en: item.name_en, es: item.name_es }, lang)}</h3>
            <p className="mt-2 text-sm text-brand-muted max-w-[22ch] leading-snug">{pick({ en: item.description_en, es: item.description_es }, lang)}</p>
          </div>
          <span className="shrink-0 font-display text-xl text-brand-gold">{item.price}</span>
        </div>
      </div>
    </motion.div>
  );
};

export const Featured = ({ items }) => {
  const { lang } = useLang();
  const featured = items.filter((i) => i.featured).slice(0, 6);
  if (!featured.length) return null;

  return (
    <section className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32" data-testid="featured">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-14">
        <div>
          <p className="text-brand-gold uppercase tracking-[0.3em] text-xs mb-4">{pick(t.featured.kicker, lang)}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-xl text-balance">
            {pick(t.featured.title, lang)}
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
        {featured.map((item, i) => (
          <Card key={item.id} item={item} index={i} />
        ))}
      </div>
    </section>
  );
};
